import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import {
  getTVConfiguration,
  updateTVConfiguration,
  TVConfiguration,
} from "../apis/tvConfig";
import { hitRefreshToken } from "../apis/user";
import { setAccessTokenForInterceptor } from "../utils/axiosUtil";
import { useUserData } from "./UserContext";

export interface TVConfigurationContextInterface {
  tvConfig: TVConfiguration | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  loadTVConfiguration: (hospitalId: string) => Promise<void>;
  updateConfiguration: (
    hospitalId: string,
    updates: Partial<TVConfiguration>
  ) => Promise<boolean>;
  clearError: () => void;
}

const getDefaultConfig = (hospitalId: string): TVConfiguration => ({
  tv_config_id: "",
  hospital_id: hospitalId,
  token: true,
  patient_name: true,
  room_number: false,
  slot: false,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  hospital_name: "",
  booking_mode: true,
});

const TVConfigurationContext = createContext<TVConfigurationContextInterface>({
  tvConfig: null,
  loading: false,
  saving: false,
  error: null,
  loadTVConfiguration: async () => {},
  updateConfiguration: async () => false,
  clearError: () => {},
});

export const TVConfigurationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [tvConfig, setTvConfig] = useState<TVConfiguration | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRequestedHospitalId, setLastRequestedHospitalId] =
    useState<string | null>(null);
  const { accessToken } = useUserData();
  const retryAttempted = useRef(false);
  const location = useLocation();

  const normalizeBoolean = (value: any): boolean => {
    if (value === true || value === false) return value;
    if (value === 1 || value === "1") return true;
    if (value === 0 || value === "0") return false;
    if (typeof value === "string") {
      const lower = value.toLowerCase();
      if (lower === "true") return true;
      if (lower === "false") return false;
    }
    return Boolean(value);
  };

  const normalizeConfig = (raw: any, hospitalId: string): TVConfiguration => {
    let profileSettings: any = null;
    try {
      const stored = localStorage.getItem("profileTVSettings");
      if (stored) {
        profileSettings = JSON.parse(stored);
      }
    } catch (error) {
      console.error(
        "Failed to parse profile TV settings from localStorage:",
        error
      );
    }

    return {
      tv_config_id: raw?.tv_config_id ?? "",
      hospital_id: raw?.hospital_id ?? hospitalId,
      token: normalizeBoolean(
        profileSettings?.token ?? raw?.token ?? true
      ),
      patient_name: normalizeBoolean(
        profileSettings?.patient_name ?? raw?.patient_name ?? true
      ),
      room_number: normalizeBoolean(
        profileSettings?.room_number ?? raw?.room_number ?? false
      ),
      slot: normalizeBoolean(
        profileSettings?.slot ?? raw?.slot ?? false
      ),
      created_at: raw?.created_at ?? new Date().toISOString(),
      updated_at: raw?.updated_at ?? new Date().toISOString(),
      hospital_name: raw?.hospital_name ?? "",
      booking_mode: normalizeBoolean(
        profileSettings?.booking_mode ?? raw?.booking_mode ?? true
      ),
    };
  };

  const refreshTokenIfNeeded = async (response: any) => {
    if (response?.status === 403 && response?.data?.message === "Token is not valid.") {
      try {
        const refresh_data = await hitRefreshToken();
        if (refresh_data?.status === 200) {
          await setAccessTokenForInterceptor(
            refresh_data.data.result.access_token
          );
          return true;
        }
        return false;
      } catch (error) {
        console.error("Context: Token refresh error:", error);
        return false;
      }
    }
    return false;
  };

  useEffect(() => {
    if (accessToken) {
      setAccessTokenForInterceptor(accessToken);
      if (lastRequestedHospitalId) {
        loadTVConfiguration(lastRequestedHospitalId);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, lastRequestedHospitalId]);

  useEffect(() => {
    const pathname = location.pathname || "";
    const segments = pathname.split("/").filter(Boolean);
    const maybeHospitalId = segments[0];

    if (
      !maybeHospitalId ||
      maybeHospitalId === "admin" ||
      maybeHospitalId === "qrsignin"
    ) {
      return;
    }

    if (!tvConfig || tvConfig.hospital_id !== maybeHospitalId) {
      loadTVConfiguration(maybeHospitalId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const clearError = () => {
    setError(null);
  };

  const loadTVConfiguration = async (hospitalId: string) => {
    if (!hospitalId) {
      return;
    }

    if (lastRequestedHospitalId !== hospitalId) {
      retryAttempted.current = false;
    }

    if (!accessToken) {
      setLastRequestedHospitalId(hospitalId);
      return;
    }

    setLoading(true);
    setError(null);
    setLastRequestedHospitalId(hospitalId);

    try {
      const response = await getTVConfiguration(hospitalId);

      if (
        response?.status === 403 &&
        response?.data?.message === "Token is not valid."
      ) {
        const tokenRefreshed = await refreshTokenIfNeeded(response);
        if (tokenRefreshed) {
          const retryResponse = await getTVConfiguration(hospitalId);
          if (
            retryResponse?.status === 200 &&
            retryResponse?.data?.status === "SUCCESS"
          ) {
            setTvConfig(
              normalizeConfig(retryResponse.data.result, hospitalId)
            );
            return;
          }
        }
      }

      if (response?.status === 200 && response?.data?.status === "SUCCESS") {
        setTvConfig(normalizeConfig(response.data.result, hospitalId));
      } else if (response?.status === 400 || response?.status === 404) {
        setTvConfig(getDefaultConfig(hospitalId));
        setError(null);
      } else if (response?.status === 401) {
        setError("Authentication required. Please log in again.");
      } else if (response?.status === 403) {
        setError("Access denied. You do not have permission to view TV settings.");
      } else {
        setTvConfig(getDefaultConfig(hospitalId));
        setError(
          response?.data?.message ||
            `Failed to load TV configuration (Status: ${response?.status})`
        );
      }
    } catch (err: any) {
      console.error("Error loading TV configuration:", err);
      setError(err?.message || "Failed to load TV configuration");
      setTvConfig(getDefaultConfig(hospitalId));
    } finally {
      setLoading(false);
    }
  };

  const updateConfiguration = async (
    hospitalId: string,
    updates: Partial<TVConfiguration>
  ): Promise<boolean> => {
    if (!hospitalId || !accessToken) return false;

    setSaving(true);
    setError(null);

    try {
      const response = await updateTVConfiguration(hospitalId, {
        token: updates.token,
        patient_name: updates.patient_name,
        room_number: updates.room_number,
        slot: updates.slot,
        booking_mode: updates.booking_mode,
      });

      if (
        response?.status === 403 &&
        response?.data?.message === "Token is not valid."
      ) {
        const tokenRefreshed = await refreshTokenIfNeeded(response);
        if (tokenRefreshed) {
          const retryResponse = await updateTVConfiguration(hospitalId, {
            token: updates.token,
            patient_name: updates.patient_name,
            room_number: updates.room_number,
            slot: updates.slot,
            booking_mode: updates.booking_mode,
          });

          if (
            retryResponse?.status === 200 &&
            retryResponse?.data?.status === "SUCCESS"
          ) {
            setTvConfig(
              normalizeConfig(retryResponse.data.result, hospitalId)
            );
            return true;
          }
        }
      }

      if (response?.status === 200 && response?.data?.status === "SUCCESS") {
        setTvConfig(normalizeConfig(response.data.result, hospitalId));
        return true;
      } else if (response?.status === 404) {
        if (tvConfig) {
          setTvConfig({
            ...tvConfig,
            token: updates.token ?? tvConfig.token,
            patient_name: updates.patient_name ?? tvConfig.patient_name,
            room_number: updates.room_number ?? tvConfig.room_number,
            slot: updates.slot ?? tvConfig.slot,
            booking_mode: updates.booking_mode ?? tvConfig.booking_mode,
            updated_at: new Date().toISOString(),
          });
        }
        return true;
      } else {
        setError(
          response?.data?.message || "Failed to update TV configuration"
        );
        if (tvConfig) {
          setTvConfig({
            ...tvConfig,
            token: updates.token ?? tvConfig.token,
            patient_name: updates.patient_name ?? tvConfig.patient_name,
            room_number: updates.room_number ?? tvConfig.room_number,
            slot: updates.slot ?? tvConfig.slot,
            booking_mode: updates.booking_mode ?? tvConfig.booking_mode,
            updated_at: new Date().toISOString(),
          });
        }
        return false;
      }
    } catch (err: any) {
      console.error("Error updating TV configuration:", err);
      setError(err?.message || "Failed to update TV configuration");
      if (tvConfig) {
        setTvConfig({
          ...tvConfig,
          token: updates.token ?? tvConfig.token,
          patient_name: updates.patient_name ?? tvConfig.patient_name,
          room_number: updates.room_number ?? tvConfig.room_number,
          slot: updates.slot ?? tvConfig.slot,
          booking_mode: updates.booking_mode ?? tvConfig.booking_mode,
          updated_at: new Date().toISOString(),
        });
      }
      return true;
    } finally {
      setSaving(false);
    }
  };

  return (
    <TVConfigurationContext.Provider
      value={{
        tvConfig,
        loading,
        saving,
        error,
        loadTVConfiguration,
        updateConfiguration,
        clearError,
      }}
    >
      {children}
    </TVConfigurationContext.Provider>
  );
};

export const useTVConfiguration = () => useContext(TVConfigurationContext);